import SearchIcon from '@mui/icons-material/Search';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { useState } from 'react';
import HighlightedFragment from '../HighlightedText/HighlightedFragment';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(0.5, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.25),
  },
  icon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  headerText: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  matchRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'baseline',
    overflow: 'hidden',
  },
  fieldLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontWeight: 500,
  },
  fragmentText: {
    fontSize: '0.7rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  toggle: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    fontStyle: 'italic',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

interface MatchContextProps {
  highlights: Record<string, string[]>;
  slugLabels?: Record<string, string>;
}

const MAX_FIELDS_SHOWN = 3;
const CONTEXT_CHARS = 20;

// Fallback: convert field slug to display label
const slugToLabel = (slug: string): string => {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Trim fragment to a window around the first <mark> so the match is always visible
const truncateAroundMatch = (fragment: string): string => {
  const markStart = fragment.indexOf('<mark>');
  if (markStart === -1) return fragment;

  const markEnd = fragment.indexOf('</mark>', markStart);
  if (markEnd === -1) return fragment;

  const before = fragment.slice(0, markStart);
  const after = fragment.slice(markEnd + 7);
  const match = fragment.slice(markStart, markEnd + 7);

  const trimBefore = before.length > CONTEXT_CHARS ? `…${before.slice(-CONTEXT_CHARS)}` : before;
  const trimAfter = after.length > CONTEXT_CHARS ? `${after.slice(0, CONTEXT_CHARS)}…` : after;

  return `${trimBefore}${match}${trimAfter}`;
};

const MatchContext: React.FC<MatchContextProps> = ({ highlights, slugLabels }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const entries = Object.entries(highlights).filter(([, fragments]) => fragments.length > 0);

  if (entries.length === 0) return null;

  const hasMore = entries.length > MAX_FIELDS_SHOWN;
  const visibleEntries = expanded ? entries : entries.slice(0, MAX_FIELDS_SHOWN);

  const handleToggle = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <Box className={classes.container}>
      <div className={classes.header}>
        <SearchIcon className={classes.icon} />
        <Typography className={classes.headerText}>Matched in:</Typography>
      </div>
      {visibleEntries.map(([slug, fragments]) => (
        <div key={slug} className={classes.matchRow}>
          <Typography className={classes.fieldLabel}>{slugLabels?.[slug] ?? slugToLabel(slug)}:</Typography>
          <Typography component="span" className={classes.fragmentText}>
            <HighlightedFragment fragment={truncateAroundMatch(fragments[0])} />
          </Typography>
        </div>
      ))}
      {hasMore && (
        <Typography className={classes.toggle} onClick={handleToggle}>
          {expanded ? 'Show less' : `+${entries.length - MAX_FIELDS_SHOWN} more`}
        </Typography>
      )}
    </Box>
  );
};

export default MatchContext;
